import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ErrorBanner } from '@/components/common/ErrorBanner';
import { useRegisterUserMutation, useSendOtpMutation } from '@/store/api/authApi';
import toast from 'react-hot-toast';
import { CustomPhoneInput } from '@/components/ui/phoneInput';
import OtpVerification from './OtpVerification';
import { useNavigate } from 'react-router-dom';
import { setUserProfile } from '@/store/slices/authSlice';
import { useLazyGetUserProfileQuery } from '@/store/api/userApi';
import { useDispatch } from 'react-redux';
import { tokenService } from '@/utils/tokenService';

const registerSchema = z
  .object({
    firstname: z.string().min(2, 'First name must be at least 2 characters'),
    lastname: z.string().min(2, 'Last name must be at least 2 characters'),
    email: z.string().min(1, 'Email is required').email('Invalid email address'),
    password: z
      .string()
      .min(6, 'Password must be at least 6 characters')
      .regex(/[A-Z]/, 'Must contain one uppercase letter')
      .regex(/[0-9]/, 'Must contain one number'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    path: ['confirmPassword'],
    message: 'Passwords do not match',
  });

type RegisterFormData = z.infer<typeof registerSchema>;

const OTP_RESEND_TIME = 120;

type PageType = 'login' | 'register' | 'forgot-password' | 'verify-otp';

interface RegisterProps {
  toggleAnimation: (newPage: PageType) => void;
}

export function Register({ toggleAnimation }: RegisterProps) {
  const [registerUser, { isLoading: registerLoading, error: registerError }] =
    useRegisterUserMutation();

  const [sendOtp, { isLoading: otpLoading, error: otpError }] = useSendOtpMutation();

  const [showPassword, setShowPassword] = useState(false);
  const [fetchUser] = useLazyGetUserProfileQuery();

  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [openOtp, setOpenOtp] = useState(false);
  const [mobileOtp, setMobileOtp] = useState('');
  const [mobileTimer, setMobileTimer] = useState(0);
  const [mobileOtpError, setMobileOtpError] = useState('');
  const [mobileOtpSent, setMobileOtpSent] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    register,
    setValue,
    trigger,
    getValues,
    setError,
    formState: { errors, isValid },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    mode: 'onChange',
    defaultValues: {
      firstname: '',
      lastname: '',
      email: '',
      password: '',
      confirmPassword: '',
    },
  });

  /* ---------------- SEND OTP ---------------- */
  const handleSendOtp = async () => {
    const isValid = await trigger([
      'firstname',
      'lastname',
      'email',
      'password',
      'confirmPassword',
    ]);
    if (!isValid) return;

    const passwordsMatch = getValues('password') === getValues('confirmPassword');
    if (!passwordsMatch) {
      setError('confirmPassword', { type: 'manual', message: 'Passwords do not match' });
      return;
    }

    setMobileOtpError('');
    setSubmitError('');

    const data = getValues();

    await sendOtp({
      email: data.email.toLowerCase(),
      type: 'register',
    }).unwrap();

    setMobileOtpSent(true);
    setMobileTimer(OTP_RESEND_TIME);
    setOpenOtp(true);
  };
  /* ---------------- REGISTER ---------------- */
  const onSubmit = async (data: RegisterFormData) => {
    if (!mobileOtpSent) {
      setMobileOtpError('Please verify your email first');
      return;
    }

    if (mobileOtp.length !== 6) {
      setMobileOtpError('Invalid OTP');
      return;
    }

    try {
      const payload = {
        firstname: data.firstname,
        lastname: data.lastname,
        email: data.email.toLowerCase(),
        password: data.password,
        emailVerificationCode: mobileOtp,
        country: 'india',
      };

      const auth = await registerUser(payload).unwrap();
      setSubmitError('');
      toast.success('Registration successful.');
      tokenService.setAccessToken(auth.accessToken);
      if (auth.refreshToken) {
        tokenService.setRefreshToken(auth.refreshToken);
      }

      const user = await fetchUser().unwrap();
      dispatch(setUserProfile(user));
      navigate('/dashboard');
    } catch (err: any) {
      console.error('Registration failed:', err?.data?.message);
      if (Array.isArray(err?.data?.message)) {
        toast.error(err?.data?.message[0]);
      } else {
        toast.error(err?.data?.message);
      }

      const message =
        err?.data?.message ||
        err?.data?.error ||
        (Array.isArray(err?.data?.errors) && err.data.errors[0]) ||
        err?.error ||
        'Something went wrong';

      if (message.toLowerCase().includes('mobile')) {
        setOpenOtp(false);
        setMobileOtpSent(false);
        setMobileOtp('');
        setMobileTimer(0);
      }

      setSubmitError(message);
    }
  };

  const submitRegister = () => {
    const data = getValues();
    onSubmit(data);
  };

  useEffect(() => {
    if (mobileTimer <= 0) return;

    const interval = setInterval(() => {
      setMobileTimer((t) => t - 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [mobileTimer]);

  return (
    <>
      {!openOtp ? (
        <div className="space-y-5 w-full max-w-[420px] mx-auto">
          {registerError && (
            <ErrorBanner
              type="error"
              title="Registration Failed"
              message={(registerError as any)?.data?.message || 'Something went wrong'}
            />
          )}

          {otpError && (
            <ErrorBanner
              type="error"
              title="OTP Error"
              message={(otpError as any)?.data?.message || 'Failed to send OTP'}
            />
          )}

          <form className="space-y-4">
            <Input {...register('firstname')} placeholder="First Name" />
            {errors.firstname && (
              <p className="text-sm text-destructive">{errors.firstname.message}</p>
            )}

            <Input {...register('lastname')} placeholder="Last Name" />
            {errors.lastname && (
              <p className="text-sm text-destructive">{errors.lastname.message}</p>
            )}

            <Input type="email" {...register('email')} placeholder="Email" />
            {errors.email && <p className="text-sm text-destructive">{errors.email.message}</p>}

            {/* <Controller
              name="mobile"
              control={control}
              render={({ field }) => (
                <CustomPhoneInput
                  value={getValues('dialCode') + field.value}
                  onChange={(mobile: string, dialCode: string, country: string) => {
                    setValue('mobile', mobile, { shouldValidate: true });
                    setValue('dialCode', dialCode);
                    setValue('country', country);
                  }}
                />
              )}
            />
            {errors.mobile && <p className="text-sm text-destructive">{errors.mobile.message}</p>} */}

            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                {...register('password')}
                placeholder="Password"
              />

              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-1 top-1/2 -translate-y-1/2"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </Button>
            </div>
            {errors.password && (
              <p className="text-sm text-destructive">{errors.password.message}</p>
            )}

            <div className="relative">
              <Input
                type={showConfirmPassword ? 'text' : 'password'}
                {...register('confirmPassword')}
                placeholder="Confirm Password"
              />

              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-1 top-1/2 -translate-y-1/2"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </Button>
            </div>
            {errors.confirmPassword && (
              <p className="text-sm text-destructive">{errors.confirmPassword.message}</p>
            )}

            <Button
              type="button"
              variant="auth"
              disabled={!isValid || registerLoading || otpLoading}
              className="w-full"
              onClick={handleSendOtp}
            >
              Generate OTP
            </Button>
          </form>

          <div className="text-center text-sm text-muted-foreground">
            Already have an account?{' '}
            <button
              onClick={(e) => {
                e.preventDefault();
                toggleAnimation('login');
              }}
              className="cursor-pointer font-medium text-[#FFB816] hover:underline"
            >
              Sign in
            </button>
          </div>
        </div>
      ) : (
        <OtpVerification
          mobileOtp={mobileOtp}
          setMobileOtp={setMobileOtp}
          mobileTimer={mobileTimer}
          mobileOtpError={mobileOtpError}
          submitError={submitError}
          handleResend={handleSendOtp}
          submit={submitRegister}
          onBack={() => setOpenOtp(false)}
          email={getValues('email')}
        />
      )}
    </>
  );
}
